import {
  modelDrawerDeploy,
  modelDrawerDownloadModel,
  modelDrawerDownloadBundledModels,
} from '@/features/trainings/components/modelDrawerTrainActions';
import { buildTaskKey } from './taskKeys';

function downloadModelAsync(training, setProgressStatus, downloadOptions) {
  return new Promise((resolve, reject) => {
    modelDrawerDownloadModel(training, setProgressStatus, downloadOptions, {
      onSuccess: () => resolve(),
      onInstancesCapError: (message) => {
        const err = new Error(message);
        err.isInstancesCapError = true;
        reject(err);
      },
      onError: (message) => reject(new Error(message)),
      onDuplicatePush: (response) => {
        const err = new Error(response?.message || 'This image was already pushed before');
        err.isDuplicatePush = true;
        err.previousPush = response?.previousPush || null;
        reject(err);
      },
    });
  });
}

function downloadBundledModelsAsync(tasks, setProgressStatus, sharedConfig) {
  return new Promise((resolve, reject) => {
    modelDrawerDownloadBundledModels(tasks, setProgressStatus, sharedConfig, {
      onSuccess: () => resolve(),
      onInstancesCapError: (message) => {
        const err = new Error(message);
        err.isInstancesCapError = true;
        reject(err);
      },
      onError: (message) => reject(new Error(message)),
    });
  });
}

function deployModelAsync(training, deploymentConfig, onDeploySuccess) {
  return new Promise((resolve, reject) => {
    modelDrawerDeploy(
      training,
      deploymentConfig,
      (deployedTraining) => {
        onDeploySuccess?.(deployedTraining);
        resolve();
      },
      {
        onInstancesCapError: (message) => {
          const err = new Error(message);
          err.isInstancesCapError = true;
          reject(err);
        },
        onError: (message) => reject(new Error(message)),
      }
    );
  });
}

/**
 * Fire download or deploy. Downloads with 2+ tasks are bundled into one
 * artifact — a single Triton image / tar for Docker, or a single ZIP
 * containing all selected models for raw files. Single-task downloads and
 * deploys run per task.
 */
export async function submitConfigureDeployment({
  mode,
  sharedConfig,
  tasks,
  artifactType = 'docker',
  setProgressStatus,
  onDeploySuccess,
  forcePush = false,
}) {
  // ECR / GCR / Docker Hub push is single-task per image tag
  // ({project}-{taskname}-v{version}); never bundle it. Multiple selected
  // tasks are pushed one image each.
  if (
    mode === 'download' &&
    tasks.length > 1 &&
    !['ecr', 'gcr', 'dockerhub'].includes(artifactType)
  ) {
    try {
      await downloadBundledModelsAsync(tasks, setProgressStatus, {
        ...sharedConfig,
        artifactType,
      });
    } catch (err) {
      err.taskKey = err.taskKey || buildTaskKey(tasks[0]?.training);
      throw err;
    }
    return;
  }

  for (const { training, config } of tasks) {
    const merged = {
      ...sharedConfig,
      ...config,
    };

    try {
      if (mode === 'download') {
        await downloadModelAsync(training, setProgressStatus, {
          artifactType,
          ...merged,
          force_push: forcePush,
        });
      } else if (mode === 'deploy') {
        await deployModelAsync(training, merged, onDeploySuccess);
      }
    } catch (err) {
      err.taskKey = buildTaskKey(training);
      throw err;
    }
  }
}

export { buildTaskKey };
